import {Selector, Component} from '@packages/systems/style/selectors';
import {PSEUDO_PREFIX} from '@packages/systems/core/constants/Selectors';
import {
  PropertyDeclarationBlock,
  convertLegacyStyleValueToString,
} from '@packages/systems/style/properties';
import type {
  DefaultStyleConfig,
  DefaultStyleDeclaration,
  ConditionalDefaultStyleDeclaration,
  BreakpointID,
  StyleRulesByBreakpointId,
} from '@packages/systems/style/types';
import {StyleRule} from '@packages/systems/style/style-sheets';

// Take an style declaration config and create style rules.
export function createRulesFromConfig(
  selectorBase: Array<Component>,
  config: DefaultStyleDeclaration,
  rulesByBreakpointId: StyleRulesByBreakpointId,
  shouldEmulatePseudoClasses: boolean
) {
  for (const mediaEntry of Object.entries(config)) {
    const breakpointId: BreakpointID = mediaEntry[0] as any;
    const ruleConfig = mediaEntry[1];

    // @ts-expect-error - TS7034 - Variable 'rules' implicitly has type 'any[]' in some locations where its type cannot be determined.
    let rules = null;
    if (rulesByBreakpointId.hasOwnProperty(breakpointId)) {
      rules = rulesByBreakpointId[breakpointId];
    } else {
      rules = [];
      // @ts-expect-error - TS7005 - Variable 'rules' implicitly has an 'any[]' type.
      rulesByBreakpointId[breakpointId] = rules;
    }

    for (const [variantKey, cssProperties] of Object.entries(ruleConfig)) {
      // Copy the base selector components for style rule selector use.
      const components = [...selectorBase];
      // Add any variant components that are needed.
      // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'PseudoType | PseudoElementType | CustomNodeStateType'.
      const pseudo = Component.createFromVariantKey(variantKey);

      // NOTE: This is a somewhat hacky way of getting the types to align until we do value type parsing.
      const styles = Object.entries(cssProperties).map(
        ([key, value]: [any, any]) => {
          return convertLegacyStyleValueToString([key, value]);
        }
      );

      if (pseudo !== null) {
        const emulatedPseudo = [
          ...components,
          Component.Class(`${PSEUDO_PREFIX}${pseudo.value}`),
        ];
        components.push(pseudo);

        if (shouldEmulatePseudoClasses) {
          // @ts-expect-error - TS2454 - Variable 'rules' is used before being assigned.
          rules.push(
            new StyleRule(
              new Selector(emulatedPseudo),
              new PropertyDeclarationBlock(styles as any)
            )
          );
        }
      }

      // @ts-expect-error - TS2454 - Variable 'rules' is used before being assigned.
      rules.push(
        new StyleRule(
          new Selector(components),
          new PropertyDeclarationBlock(styles as any)
        )
      );
    }
  }
  return rulesByBreakpointId;
}

export const ATOMS_REQUIRING_FOCUS_HACK = [
  'CommerceCheckoutCardSecurityCode',
  'CommerceCheckoutCardExpirationDate',
  'CommerceCheckoutCardNumber',
] as const;

function appendFocusHacks(
  defaultStyleConfig: DefaultStyleConfig,
  atomClassName: string,
  rulesByBreakpointId: StyleRulesByBreakpointId
) {
  if (defaultStyleConfig.style == null) {
    return;
  }
  // @ts-expect-error - TS2339 - Property 'focus' does not exist on type 'Partial<Record<"lang" | "active" | "placeholder" | "focus" | "after" | "before" | "hover" | "empty" | "noPseudo" | "nth-child(odd)" | "nth-child(even)" | "first-child" | "last-child" | "pressed" | "visited" | "focus-visible" | "focus-within", WebflowCssProperties>> | undefined'.
  const {focus} = defaultStyleConfig.style.base.main;
  const {main} = rulesByBreakpointId;

  if (focus && main) {
    const styles = (Object.entries(focus) as Array<[any, any]>).map(
      convertLegacyStyleValueToString
    );

    main.push(
      new StyleRule(
        new Selector([
          Component.Class(atomClassName),
          Component.Class('-wfp-focus'),
        ]),
        new PropertyDeclarationBlock(styles)
      )
    );
  }
}

// Convert an default style config into style rules.
export function createDefaultRulesFromConfig(
  defaultStyleConfig: DefaultStyleConfig,
  options?: {
    emulatePseudos?: boolean;
  }
): StyleRulesByBreakpointId {
  const {emulatePseudos = false} = options || {};
  const rulesByBreakpointId: StyleRulesByBreakpointId = {};
  if (defaultStyleConfig.style == null) {
    return rulesByBreakpointId;
  }

  const {base, conditional} = defaultStyleConfig.style;
  const defaultStyleClassName = toDefaultClassName(defaultStyleConfig);

  if (base) {
    // Create style rules from a style config using the namespaced default style
    // class name as the base selector component.
    createRulesFromConfig(
      [Component.Class(defaultStyleClassName)],
      base,
      rulesByBreakpointId,
      emulatePseudos
    );
  }

  if (conditional) {
    createDefaultRulesFromConditionalConfig(
      defaultStyleConfig.defaultKey,
      defaultStyleClassName,
      conditional,
      rulesByBreakpointId,
      emulatePseudos
    );
  }

  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"CommerceCheckoutCardExpirationDate" | "CommerceCheckoutCardNumber" | "CommerceCheckoutCardSecurityCode"'.
  if (ATOMS_REQUIRING_FOCUS_HACK.includes(defaultStyleConfig.defaultKey)) {
    appendFocusHacks(
      defaultStyleConfig,
      defaultStyleClassName,
      rulesByBreakpointId
    );
  }

  return rulesByBreakpointId;
}

function createDefaultRulesFromConditionalConfig(
  defaultKey: string,
  defaultClassName: string,
  config: ConditionalDefaultStyleDeclaration,
  rulesByBreakpointId: StyleRulesByBreakpointId,
  shouldEmulatePseudoClasses: boolean
): StyleRulesByBreakpointId {
  for (const [conditionalKey, valueConfig] of Object.entries(config)) {
    for (const [valueKey, ruleConfig] of Object.entries(valueConfig)) {
      const conditionalClassName = conditionalDefaultClassName(
        defaultClassName,
        conditionalKey,
        valueKey
      );

      createRulesFromConfig(
        [Component.Class(conditionalClassName)],
        ruleConfig,
        rulesByBreakpointId,
        shouldEmulatePseudoClasses
      );
    }
  }

  return rulesByBreakpointId;
}

// Helper functions for deriving a classname from an default style config.
export function toDefaultClassName(
  defaultStyleConfig: DefaultStyleConfig
): string {
  const name = getClassNameFromDefaultStyleConfig(defaultStyleConfig);
  const namespace = getNamespaceFromDefaultStyleConfig(defaultStyleConfig);
  return namespace === null
    ? toClassName(name)
    : toClassName(`w-${namespace}-${name}`);
}

export function conditionalDefaultClassName(
  defaultClassName: string,
  conditionalKey: string,
  conditionalValue: string
): string {
  return `${defaultClassName}--${conditionalKey}-${conditionalValue}`;
}

function getClassNameFromDefaultStyleConfig(
  defaultStyleConfig: DefaultStyleConfig
): string {
  return defaultStyleConfig.className || defaultStyleConfig.defaultKey;
}

function getNamespaceFromDefaultStyleConfig(
  defaultStyleConfig: DefaultStyleConfig
): string | null {
  return defaultStyleConfig.className === undefined
    ? defaultStyleConfig.pluginKey
    : null;
}

// Partially ported from @packages/systems/style-legacy/utils/css.js
// Convert to a valid CSS class doing the following:
// - lowercase the string
// - replace invalid characters with a ' '
// - replace any leading or trailing ' ', '_', '-' with empty string
// - replace any remaining whitespace with '-'
function toClassName(string: string): string {
  return string
    .toLowerCase()
    .replace(/[^0-9a-z_-]/g, ' ')
    .replace(/^[ _-]+|[ _-]+$/g, '')
    .replace(/\s+/g, '-');
}
